import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {getFormattedNumber} from "../helpers/getFormattedNumber";

export interface TwoColumnTableItem {
  color: string;
  label: string;
  value: string | number;
}

export interface TwoColumnTableProps {
  firstHeaderLabel: string;
  secondHeaderLabel: string;
  items: TwoColumnTableItem[];
}

const Container = styled.div`
  padding: 20px;
  border-radius: 5px;
  border: 
    1px
    solid
    ${({theme}) => theme.variables.borderColor};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 50%);
`;

const Cell = styled.div`
  position: relative;
  display: grid;
  gap: 10px;
  align-items: center;
  padding: 8px 0 8px 10px;

  border-bottom: 1px solid ${({theme}) => theme.variables.borderColor};

  &:first-child {
    padding-left: 18px;
  }
`;

const Dot = styled.div<{color: string}>`
  position: absolute;
  left: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({color}) => color};
`

const Header = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 50%);
  
  ${Cell} {
    padding-top: 0;
    padding-bottom: 12px;
    text-transform: uppercase;
    
    font-size: 10px;
    font-weight: 500;
    line-height: 13px;
    letter-spacing: 0.03em;
    color: ${({theme}) => transparentize(.6, theme.variables.color)};
  }
`;

const Body = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: ${({theme}) => theme.variables.color};
`;

/**
 * The component render table
 * @param {TableProps} props
 * @return {React.ReactElement<TableProps>}
 * @constructor
 */
export default function TwoColumnTable(
    props: TwoColumnTableProps,
): React.ReactElement {
  /**
   * Render table header
   */
  function renderHeader() {
    return (
      <Header>
        <Cell>{props.firstHeaderLabel}</Cell>
        <Cell>{props.secondHeaderLabel}</Cell>
      </Header>
    )
  }

  /**
   * Render table body
   */
  function renderBody() {
    return (
      <Body>
        {props.items.map(renderItem)}
      </Body>
    )
  }

  /**
   * Render item row
   * @param {TwoColumnTableItem} item
   */
  function renderItem(item: TwoColumnTableItem) {
    return (
      <Row>
        {renderLabelCell(item)}
        {renderValueCell(item)}
      </Row>
    )
  }


  /**
   * Render cell with item label
   * @param {TwoColumnTableItem} item
   */
  function renderLabelCell(item: TwoColumnTableItem) {
    return (
      <Cell>
        <Dot color={item.color} />
        {item.label}
      </Cell>
    )
  }

  /**
   * Render cell with item value
   * @param {TwoColumnTableItem} item
   */
  function renderValueCell(item: TwoColumnTableItem) {
    const value = typeof item.value === 'number'
      ? getFormattedNumber(item.value)
      : item.value;

    return (
      <Cell>{value}</Cell>
    )
  }

  return (
    <Container>
      {renderHeader()}
      {renderBody()}
    </Container>
  );
}
